/**
 * The diagnosis: what broke, where on the pipeline, and what would close it, in fault-report
 * order. Reasoning rather than answer lives in the Evidence panel.
 */

import { ArrowRight, Send } from 'lucide-react';
import { humanize } from '@/modules/simulator/ui/text';
import type { AdditionalDefect, DeliveryBlock, FixSuggestion, VerdictReport } from '../report-data';
import { ExpectedActual } from './expected-actual';
import { LadderRail } from './ladder-rail';
import { Chip, ReportSection, Stagger } from './report-bits';

/** A run can answer perfectly and still fail here, by sending a draft a human was meant to send. */
const Delivery = ({ delivery }: { delivery: DeliveryBlock }) => (
  <div className='flex items-start gap-2.5 rounded-xl border border-gray-500 bg-white px-3.5 py-2.5'>
    <Send className='mt-0.5 size-3.5 shrink-0 text-gray-800' aria-hidden />
    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
      <div className='flex items-center gap-2'>
        <span className='text-[11px] font-medium tracking-wide text-gray-800 uppercase'>Delivery</span>
        <Chip tone={delivery.status === 'broken' ? 'solid' : 'outline'}>{humanize(delivery.status)}</Chip>
      </div>
      {delivery.note && <p className='text-xs leading-4.5 text-text-body'>{delivery.note}</p>}
    </div>
  </div>
);

const Fixes = ({ fixes }: { fixes: FixSuggestion[] }) => (
  <ol className='flex flex-col gap-2'>
    {fixes.map((fix, index) => (
      <li key={index} className='flex items-start gap-2.5 rounded-xl border border-gray-500 bg-white px-3.5 py-2.5'>
        <span className='mt-px flex size-4.5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[10px] font-medium text-indigo-700'>
          {index + 1}
        </span>
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <p className='text-[13px] leading-5 text-text-body'>{fix.text}</p>
          {fix.targetType && (
            <Chip mono className='w-fit'>
              {fix.targetType}
            </Chip>
          )}
        </div>
      </li>
    ))}
  </ol>
);

/** A second, independently fixable defect; optional on the envelope and usually absent. */
const Defects = ({ defects }: { defects: AdditionalDefect[] }) => (
  <div className='flex flex-col gap-2'>
    {defects.map((defect, index) => (
      <div key={index} className='flex flex-col gap-2 rounded-xl border border-gray-500 bg-white px-3.5 py-3'>
        <div className='flex flex-wrap items-center gap-2'>
          {defect.rung && <Chip tone='accent'>{humanize(defect.rung)}</Chip>}
          {defect.fixKey && <Chip mono>{defect.fixKey}</Chip>}
          {defect.confidence !== null && (
            <span className='text-[11px] text-gray-800 tabular-nums'>{Math.round(defect.confidence * 100)}%</span>
          )}
        </div>
        {defect.summary && <p className='text-[13px] leading-5 text-text-body'>{defect.summary}</p>}
        <ExpectedActual expected={defect.expected} actual={defect.actual} />
        {defect.fixes.length > 0 && <Fixes fixes={defect.fixes} />}
      </div>
    ))}
  </div>
);

export const DiagnosisPanel = ({ report }: { report: VerdictReport }) => (
  <Stagger className='gap-6'>
    {[
      <ExpectedActual key='ea' expected={report.expected} actual={report.actual} />,

      report.ladder.length > 0 ? (
        <ReportSection
          key='ladder'
          title='Pipeline'
          aside={
            report.firstBrokenRung ? (
              <span className='flex items-center gap-1.5 text-[11px] text-gray-800'>
                broke at
                <ArrowRight className='size-3' aria-hidden />
                <span className='font-medium text-text-heading'>{humanize(report.firstBrokenRung)}</span>
              </span>
            ) : (
              <span className='text-[11px] text-gray-800'>every rung held</span>
            )
          }
        >
          <div className='rounded-xl border border-gray-500 bg-white px-4 pt-4 pb-3'>
            <LadderRail ladder={report.ladder} />
          </div>
          {report.delivery && <Delivery delivery={report.delivery} />}
        </ReportSection>
      ) : null,

      report.rootCause ? (
        <ReportSection key='cause' title='Root cause'>
          <p className='text-[13px] leading-5 text-text-body'>{report.rootCause}</p>
        </ReportSection>
      ) : null,

      report.fixes.length > 0 ? (
        <ReportSection key='fixes' title='Suggested fixes'>
          <Fixes fixes={report.fixes} />
        </ReportSection>
      ) : null,

      report.additionalDefects.length > 0 ? (
        <ReportSection
          key='defects'
          title='Other defects'
          aside={<span className='text-[11px] text-gray-800'>{report.additionalDefects.length}</span>}
        >
          <Defects defects={report.additionalDefects} />
        </ReportSection>
      ) : null,
    ]}
  </Stagger>
);
